import patientMedicationQuestion from "./patientMedicationQuestion";

// CHANGELOG (Reverse chronological order)
// Version 2026103101
// - Add intake-concept annotations: `concept` on each question and on each
//   mapped choice (e.g. concept: "sleep.onset_latency_min",
//   concept: "condition.menopausal"). Saved with every answer, they let the
//   intake-observation extractor (app/service/intakeconcepts) read concepts
//   directly instead of matching wording. Titles, choices, codes and order are
//   unchanged; patients see no difference. See
//   docs/intake-concepts-and-phenotypes.md.
//
// Version 2026102001
// - Give every question a unique code: sleep-bedtime-habits, substance-use,
//   cage-aid, doctor-let-know-radio. "Do you have any medical conditions?"
//   moves from "medical-conditions" (which its free-text child also used) to
//   medical-conditions-radio; the child keeps "medical-conditions". Titles and
//   choices are unchanged.
//
// Version 2026101201
// - Preserve Prazosin spelling corrections alongside confirmed patient medication history.
//
// Version 2026100801
// - Search medications locally and confirm optional dose/frequency details with Save and continue.
//
// Version 2026100101
// - Add confirmed patient medication history with name, dose, and frequency.

import { getVisitUSState } from "~/visits/utils/visitCookies";

import { isStateControlledMedication } from "~/shared/utils/sharedUtils";
import { isPDMPSleepMedication } from "../assets/js/bundles/shared/utils/sharedUtils";

const CONTROLLED_MED_NOTICE = `<div class="controlled-medication-disclaimer">Cannot be prescribed online because it is a controlled medication in your state</div>`;
const PDMP_MED_DISCLAIMER = `<div class="controlled-medication-disclaimer">Cannot be prescribed online in your state as it is a monitored prescription medication</div>`;

// So the first set of questions above looks good as a general screen.
// The second set, is more tailored for propranolol and cardiac / respiratory issues. I would add a "Do you have any history of cardiac issues," to the top list.
// For the bottom list

// 0. Yes to screen for
// PHQ2, GAD2
// OSA,
// EDS,
// 1. Bipolar or mental health conditions
// 2. Alcohol or substance abuse
// 3. history of car accidents, or mistakes due to excessive daytime or morning sleepiness
// 4. Nasal congestion or mouth breathing
// 5. Parasomnias - or strange behaviors at night - sleep walking, sleep talking, bedwetting, etc
// 6. Morning headaches, palpitations, or waking at night (another sleep apnea screen)
// 7. A history of dependence on anxiety or sleep medications

// Do we have a STOP BANG, PHQ9, and Eppworth Sleepiness Scale in there already?

export default function () {
  return {
    name: "Insomnia Screening",
    description:
      "We can help you find out if medication might help with your insomnia",
    version: "2026103101",
    service_code: "INSOMNIA",
    live: false,
    staged: true,
    questions: [
      {
        concept: "motivation.why",
        title: "Why are you interested in treatment for insomnia?",
        code: "why-interested",
        description:
          "This helps us understand and recommend the right treatment for you.",
        type: "checkbox",
        choices: [
          {
            concept: "motivation.why.tried_other_without_success",
            label: "Have tried other treatment options without success",
          },
          {
            concept: "motivation.why.looking_for_medication",
            label: "Looking for a medication to treat your insomnia symptoms",
          },
          {
            concept: "motivation.why.heard_from_someone",
            label: "Heard about medications from someone around you",
          },
          {
            concept: "motivation.why.heard_in_news",
            label: "Heard about medications in the news",
          },
          {
            concept: "motivation.why.used_kick_before",
            label: "I've used Kick in the past",
          },
          {
            concept: "motivation.why.internet_research",
            label: "Research on the internet",
          },
          {
            concept: "motivation.why.other",
            label: "Other",
            children: [
              {
                title: "Please tell us why you're interested...",
                type: "text",
                code: "why-interested-other",
              },
            ],
          },
        ],
      },

      {
        concept: "sleep.complaint",
        title:
          "Which of the following sleep difficulties are you experiencing?",
        code: "insomnia-disorder-screening",
        description:
          "Certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know everything going on with your health.",
        type: "checkbox",
        choices: [
          {
            concept: "sleep.complaint.onset",
            label: "Difficulty falling asleep / initiating sleep",
          },
          {
            concept: "sleep.complaint.maintenance",
            label: "Difficulty staying asleep / maintaining sleep",
          },
          {
            concept: "sleep.complaint.early_waking",
            label: "Early morning awakening",
          },
          {
            concept: "sleep.complaint.daytime_distress",
            label:
              "Sleep issues are causing work, school, social or behavioral distress",
          },
          {
            concept: "sleep.complaint.three_nights_week",
            label: "Sleep issues occur at least 3 nights per week or more",
          },
          {
            concept: "sleep.complaint.despite_opportunity",
            label: "Unable to sleep even if the setting and time allow it",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        concept: "treatment.nonrx",
        title:
          "What have you tried to help with your sleep difficulties? (Check all that apply)",
        code: "insomnia-other-treatments-tried",
        description:
          "Please be as detailed as possible - this helps us understand what you've already tried and customize a treatment plan personalized to you.",
        type: "checkbox",
        choices: [
          {
            concept: "treatment.nonrx.melatonin",
            label: "Melatonin",
          },
          {
            concept: "treatment.nonrx.otc_sleep_aid",
            label:
              "Over the counter sleep aids (e.g. Benadryl, Unisom, Tylenol PM, etc.)",
            children: [
              {
                title: "Tell us more about the over the counter sleep aids",
                description:
                  "Tell us more about the over the counter sleep aids",
                code: "insomnia-other-treatments-tried-sleep-aids",
                type: "textarea",
              },
            ],
          },
          {
            concept: "treatment.nonrx.supplements",
            label:
              "Supplements or vitamins (e.g. Magnesium, GABA, Ashwagandha, Valerian root, etc.)",
            children: [
              {
                title:
                  "Which supplements or vitamins have you tried? Are you currently taking any?",
                description:
                  "Which supplements or vitamins have you tried? Are you currently taking any?",
                code: "insomnia-other-treatments-tried-supplements",
                type: "textarea",
              },
            ],
          },
          {
            concept: "treatment.nonrx.sleep_hygiene",
            label:
              "Sleep Hygiene (e.g. no screens before bed, no caffeine, etc.)",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-sleep-hygiene",
                type: "textarea",
              },
            ],
          },
          {
            concept: "treatment.nonrx.gadgets",
            label: "Sleep gadgets (e.g. Fitbit, Oura, Whoop, etc.)",
          },
          {
            concept: "treatment.nonrx.apps",
            label:
              "Mobile Apps (e.g. Calm, Headspace, Sleep Cycle, SleepScore, etc.)",
            children: [
              {
                title: "Which ones? How'd it go?",
                description: "Which ones? How'd it go?",
                code: "insomnia-other-treatments-tried-mobile-apps",
                type: "textarea",
              },
            ],
          },
          {
            concept: "treatment.nonrx.behavioral",
            label:
              "Meditation, relaxation, or other behavioral techniques (e.g. CBT-I)",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-behavioral-techniques",
                type: "textarea",
              },
            ],
          },
          {
            concept: "treatment.nonrx.other",
            label: "Other",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-other",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "rx.tried",
        title: "What prescription sleep medications have you used?",
        code: "insomnia-prescripton-medications-tried",
        description:
          "Let us know the medications you have used before so your doctor can personalize your sleep treatment plan.",
        type: "checkbox",
        choices: [
          { concept: "rx.tried.zolpidem", label: "Ambien / Zolpidem" },
          {
            concept: "rx.tried.cyclobenzaprine",
            label: "Cyclobenzaprine",
            children: [
              {
                title: "Did Cyclobenzaprine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-cyclobenzaprine-detail",
              },
            ],
          },
          { concept: "rx.tried.lemborexant", label: "Dayvigo" },
          {
            concept: "rx.tried.doxepin",
            label: "Doxepin / Silenor",
            children: [
              {
                title: "Did Doxepin work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-doxepin-detail",
              },
            ],
          },
          {
            concept: "rx.tried.gabapentin",
            label: "Gabapentin",
            children: [
              {
                title: "Did Gabapentin work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-gabapentin-detail",
              },
            ],
          },
          {
            concept: "rx.tried.guanfacine",
            label: "Guanfacine",
            children: [
              {
                title: "Did Guanfacine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-guanfacine-detail",
              },
            ],
          },
          {
            concept: "rx.tried.hydroxyzine",
            label: "Hydroxyzine",
            children: [
              {
                title: "Did Hydroxyzine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-hydroxyzine-detail",
              },
            ],
          },
          { concept: "rx.tried.eszopiclone", label: "Lunesta / Eszopiclone" },
          {
            concept: "rx.tried.prazosin",
            label: "Prazosin / Minipress",
            children: [
              {
                title: "Did Prazosin work?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-prozasin-detail",
              },
            ],
          },
          {
            concept: "rx.tried.quetiapine",
            label: "Quetiapine / Seroquel",
            children: [
              {
                title: "Did Quetiapine work?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-quetiapine-detail",
              },
            ],
          },
          {
            concept: "rx.tried.ramelteon",
            label: "Ramelteon / Rozerem",
            children: [
              {
                title: "Did Ramelteon work?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-ramelteon-detail",
              },
            ],
          },
          { concept: "rx.tried.temazepam", label: "Restoril / Temazepam" },
          { concept: "rx.tried.zaleplon", label: "Sonata / Zaleplon" },
          {
            concept: "rx.tried.trazodone",
            label: "Trazodone",
            children: [
              {
                title: "Did Trazodone work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-trazodone-detail",
              },
            ],
          },
          {
            concept: "rx.tried.other",
            label: "Other",
            children: [
              {
                title:
                  "Which prescription medications and dosage did you try? Did it work?",
                code: "insomnia-prescripton-medications-tried-other",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "preference.interested",
        title:
          "Are there any sleep medications you are interested in learning more about?",
        code: "medications-interested-in-checkbox",
        description:
          "Please note we do not prescribe controlled medications such as Ambien or Lunesta.",
        type: "checkbox",
        choices: [
          {
            concept: "preference.interested.cyclobenzaprine",
            label: `Cyclobenzaprine${
              isStateControlledMedication(getVisitUSState(), "cyclobenzaprine")
                ? CONTROLLED_MED_NOTICE
                : isPDMPSleepMedication(getVisitUSState(), "cyclobenzaprine")
                  ? PDMP_MED_DISCLAIMER
                  : ""
            }`,
            disabled:
              isStateControlledMedication(
                getVisitUSState(),
                "cyclobenzaprine",
              ) || isPDMPSleepMedication(getVisitUSState(), "cyclobenzaprine"),
          },
          { concept: "preference.interested.doxepin", label: "Doxepin" },
          {
            concept: "preference.interested.gabapentin",
            label: `Gabapentin${
              isStateControlledMedication(getVisitUSState(), "gabapentin")
                ? CONTROLLED_MED_NOTICE
                : isPDMPSleepMedication(getVisitUSState(), "gabapentin")
                  ? PDMP_MED_DISCLAIMER
                  : ""
            }`,
            children: [
              {
                title: "Could you tell us why you're interested in Gabapentin?",
                code: "insomnia-medications-interested-gabapentin-detail",
                type: "textarea",
              },
            ],
            disabled:
              isStateControlledMedication(getVisitUSState(), "gabapentin") ||
              isPDMPSleepMedication(getVisitUSState(), "gabapentin"),
          },
          { concept: "preference.interested.guanfacine", label: "Guanfacine" },
          {
            concept: "preference.interested.hydroxyzine",
            label: "Hydroxyzine",
          },
          { concept: "preference.interested.prazosin", label: "Prazosin" },
          { concept: "preference.interested.quetiapine", label: "Quetiapine" },
          { concept: "preference.interested.ramelteon", label: "Ramelteon" },
          { concept: "preference.interested.trazodone", label: "Trazodone" },
          {
            label: "No - Let my doctor guide me",
            silent: true,
          },
        ],
      },
      {
        concept: "condition.any_reported",
        title: "Do you have any medical conditions?",
        code: "medical-conditions-radio",
        description:
          "Include even any minor or recent conditions or symptoms. To give you the best care we need to know your full history.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What medical conditions do you have?",
                description:
                  "Even include things you think you have whether or not you've been diagnosed by your doctor. We will message or call you if we need more information.",
                code: "medical-conditions",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, don't have any medical conditions",
            silent: true,
          },
        ],
      },
      {
        concept: "history.surgery_any",
        title: "Have you ever had any surgeries or hospitalizations?",
        code: "surgeries-hospitalizations",
        description:
          "Include any significant emergency room visits, hospital stays, or surgeries, including cosmetic surgeries.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What surgeries or hospitalizations have you had?",
                description:
                  "List all the procedures and events and try to remember when they occurred.",
                code: "hospitalizations",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, never had surgery or been hospitalized",
            silent: true,
          },
        ],
      },
      patientMedicationQuestion,
      {
        concept: "allergy.any",
        title: "Do you have any allergies or medication reactions?",
        code: "allergies-medication-reactions",
        description:
          "Please include any allergies or major reactions to medicines.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What allergies do you have?",
                description:
                  "List the allergies and if possible, the severity of any reaction you have had.",
                code: "allergies",
                type: "textarea", // @todo multi-select autocomplete
              },
            ],
          },
          {
            label: "No, don't have any allergies or medication reactions",
            silent: true,
          },
        ],
      },

      {
        concept: "complaint.text",
        title:
          "Tell us in your own words what sleep problems you are experiencing and how you feel?",
        description:
          "Please provide as much information as possible so your doctor can perform a thorough evaluation.",
        type: "textarea",
        code: "chief-complaint",
      },
      {
        concept: "sleep.chronotype",
        title: "Do you consider yourself a morning person or a night person?",
        code: "sleep-chronotype",
        description: "",
        type: "radio",
        choices: [
          { label: "Morning person" },
          { label: "Night person" },
          { label: "Not sure" },
        ],
      },
      {
        concept: "sleep.bedtime.ideal_hour",
        title: "What's your ideal bedtime?",
        description: "",
        type: "radio",
        code: "sleep-bed-time-ideal",
        choices: [
          {
            label: "Earlier than 7pm",
          },
          {
            label: "7pm",
          },
          {
            label: "8pm",
          },
          {
            label: "9pm",
          },
          {
            label: "10pm",
          },
          {
            label: "11pm",
          },
          {
            label: "12am",
          },
          {
            label: "1am",
          },
          {
            label: "2am",
          },
          {
            label: "3am",
          },
          {
            label: "4am",
          },
          {
            label: "Later than 4am",
          },
        ],
      },
      {
        concept: "sleep.bedtime.actual_hour",
        title: "What time do you find yourself actually going to bed?",
        description:
          "If different from your ideal bedtime, what time do you find yourself getting to bed?",
        type: "radio",
        code: "sleep-bed-time-actual",
        choices: [
          {
            label: "earlier than 7pm",
          },
          {
            label: "7pm",
          },
          {
            label: "8pm",
          },
          {
            label: "9pm",
          },
          {
            label: "10pm",
          },
          {
            label: "11pm",
          },
          {
            label: "12am",
          },
          {
            label: "1am",
          },
          {
            label: "2am",
          },
          {
            label: "3am",
          },
          {
            label: "4am",
          },
          {
            label: "later than 4am",
          },
        ],
      },
      {
        concept: "sleep.waketime.ideal_hour",
        title: "What's your ideal wake up time?",
        description: "",
        type: "radio",
        code: "sleep-wake-time-ideal",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        concept: "sleep.waketime.workday_hour",
        title:
          "What time do you normally wake up on weekdays? (Monday - Friday) ",
        description: "",
        type: "radio",
        code: "sleep-wake-time-weekday",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        concept: "sleep.waketime.free_day_hour",
        title: "What time do you wake up on weekends (Saturday / Sunday)?",
        description: "",
        type: "radio",
        code: "sleep-wake-time-weekends",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        concept: "sleep.onset_latency_min",
        title: "How long does it take you to fall asleep once in bed?",
        description: "",
        type: "radio",
        code: "sleep-fall-asleep-time",
        choices: [
          {
            label: "Less than 5 mins",
          },
          {
            label: "15 minutes",
          },
          {
            label: "30 minutes",
          },
          {
            label: "1 hour",
          },
          {
            label: "1.5 hours",
          },
          {
            label: "2 hours",
          },
          {
            label: "More than 2 hours",
          },
        ],
      },
      {
        concept: "behavior.bedtime",
        title: "Do any of the following bedtime habits apply to you?",
        code: "sleep-bedtime-habits",
        description: "This helps us understand your current bedtime routines",
        type: "checkbox",
        choices: [
          {
            concept: "behavior.bedtime.computer",
            label: "Use a computer an hour or less before going to bed",
          },
          {
            concept: "behavior.bedtime.phone",
            label: "Use your phone right before bed",
          },
          {
            concept: "behavior.bedtime.bright_light",
            label:
              "Have bright light exposure in your bedroom or home before bed",
          },
          {
            concept: "behavior.bedtime.caffeine_after_3pm",
            label: "Drink coffee or use caffeine after 3pm",
          },
          {
            concept: "behavior.bedtime.alcohol_before_bed",
            label: "Drink alcohol at night or before bed",
          },
          {
            concept: "behavior.bedtime.evening_exercise",
            label: "Exercise in the evening",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        concept: "sleep.awakenings_per_night",
        title:
          "On average, how many times a night do you wake up in the middle of the night?",
        code: "sleep-wake-ups-per-night",
        description:
          "This helps us understand your current sleep quality and offer suggestions to improve it",
        type: "radio",
        choices: [
          { label: "0" },
          { label: "1-2" },
          { label: "3-4" },
          { label: "5 or more" },
        ],
      },

      {
        concept: "sleep.awakening_timing",
        title:
          "If you wake up in the middle of the night, How soon after you fall asleep are you waking up?",
        code: "sleep-wake-ups-how-soon-after-falling-asleep",
        description:
          "This helps us understand your current sleep quality and offer suggestions to improve it",
        type: "checkbox",
        choices: [
          { concept: "sleep.awakening_timing.h1_2", label: "1-2 hours" },
          { concept: "sleep.awakening_timing.h2_4", label: "2-4 hours" },
          { concept: "sleep.awakening_timing.h4_6", label: "4-6 hours" },
          { concept: "sleep.awakening_timing.h6_8", label: "6-8 hours" },
          {
            concept: "sleep.awakening_timing.not_applicable",
            label: "Not applicable",
            silent: true,
          },
        ],
      },

      {
        concept: "sleep.return_to_sleep_min",
        title:
          "If you wake up in the middle of the night, how long does it take you to fall back asleep?",
        code: "sleep-wake-ups-time-to-fall-back-asleep",
        description: "",
        type: "radio",
        choices: [
          { label: "5 minutes or less" },
          { label: "15 minutes" },
          { label: "30 minutes" },
          { label: "An hour" },
          { label: "More than an hour" },
          {
            concept: "sleep.return_to_sleep.not_applicable",
            label: "Not applicable",
            silent: true,
          },
        ],
      },

      {
        concept: "condition",
        title: "Do you have any of the following conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "conditions",
        choices: [
          {
            concept: "condition.circulation",
            label:
              "Any problems with poor circulation, or cold or blue extremities?",
          },
          {
            concept: "condition.pregnant",
            label: "Are you pregnant?",
          },
          {
            concept: "condition.breastfeeding",
            label: "Are you breastfeeding?",
          },
          {
            concept: "condition.menopausal",
            label: "Are you menopausal?",
          },
          {
            concept: "condition.bp_medication",
            label: "Do you take any medication to lower blood pressure?",
            children: [
              {
                title:
                  "Please list your blood pressure medications and dosages if you know them.",
                description:
                  "list your blood pressure medications and dosages if you know them",
                code: "blood-pressure-medication-details",
                type: "textarea",
              },
            ],
          },
          {
            concept: "condition.liver",
            label: "Any liver problems?",
          },
          {
            concept: "condition.kidney",
            label: "Any kidney problems?",
          },
          {
            concept: "condition.cardiac",
            label: "Any history of cardiac/heart issues?",
            children: [
              {
                title: "Tell us more about the heart condition / problem...",
                description: "Tell us more about the heart condition / problem",
                code: "history-heart-condition-detail",
                type: "textarea",
              },
            ],
          },
          {
            concept: "condition.sexual_dysfunction",
            label:
              "Are you currently experiencing any symptoms of sexual dysfunction?",
          },
          {
            concept: "condition.glaucoma",
            label: "Do you have glaucoma?",
          },
          {
            concept: "condition.mental_disorder",
            label:
              "Ever been diagnosed with any mental disorders such as bipolar disorder or schizophrenia?",
            children: [
              {
                title: "What mental disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "mental-disorders",
                type: "textarea",
              },
            ],
          },
          {
            concept: "condition.neurological",
            label:
              "Ever been diagnosed with any neurological disorder such as Parkinson’s disease or dementia?",
            children: [
              {
                title:
                  "What neurological disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "neurological-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "sleep.ruleout",
        title:
          "Have you ever had any of the following sleep-related conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "sleep-rule-outs",
        choices: [
          {
            concept: "sleep.ruleout.narcolepsy_family_history",
            label: "Any family history of narcolepsy?",
          },
          {
            concept: "sleep.ruleout.irresistible_sleep_urge",
            label:
              "Any recurring episodes, more than 3 times a week over the last 3 months, of an irresistible urge to sleep?",
          },
          {
            concept: "sleep.ruleout.cataplexy",
            label:
              "Episodes of sudden muscle weakness, precipitated by laughter or joking?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "cataplexy-details",
              },
            ],
          },
          {
            concept: "sleep.ruleout.drowsy_accidents",
            label:
              "Any history of car accidents or mistakes due to excessive daytime or morning sleepiness?",
            children: [
              {
                title: "Tell us what happened...",
                type: "text",
                code: "car-accident-details",
              },
            ],
          },
          {
            concept: "sleep.ruleout.nasal_congestion",
            label: "Any history of nasal congestion or mouth breathing?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "mouth-breathing-details",
              },
            ],
          },
          {
            concept: "sleep.ruleout.morning_headache_palpitations",
            label:
              "Any morning headaches, heart palpitations, or waking up suddenly at night?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "sleep-apnea-morning-headache-screen-details",
              },
            ],
          },
          {
            concept: "history.sedative_dependence",
            label: "Any history of dependence on anxiety or sleep medications?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "anxiety-sleep-medication-dependence-details",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "sleep.parasomnia",
        title:
          "Have you ever had any of the following strange behaviors in your sleep?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "parasomnia-rule-outs",
        choices: [
          {
            concept: "sleep.parasomnia.nrem",
            label:
              "Any history of strange behaviors at night such as sleepwalking, sleep talking, eating in your sleep, or bedwetting?",
            children: [
              {
                title: "What strange behaviors have you experienced?",
                type: "text",
                code: "parasomnia-behavior-details",
              },
            ],
          },
          {
            concept: "sleep.parasomnia.rbd",
            label:
              "Any episodes of acting out dreams? Kicking or thrashing while asleep?",
            children: [
              {
                title: "What have you experienced?",
                type: "text",
                code: "parasomnia-acting-out-details",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "stopbang",
        title:
          "Have you ever had any of the following snoring or sleep-apnea related conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "stop-bang",
        choices: [
          {
            concept: "stopbang.snore",
            label: "Do you snore loudly?",
            children: [
              {
                title:
                  "Tell us more - how often do you snore, and how loud is it? Any treatment?",
                description:
                  "How often do you snore, and how loud is it? Any treatment?",
                code: "history-stopbang-snoring-detail",
                type: "textarea",
              },
            ],
          },
          {
            concept: "stopbang.tired",
            label: "Do you often feel tired or sleepy during the day?",
          },
          {
            concept: "stopbang.observed_apnea",
            label:
              "Has anyone ever observed you stop breathing or gasping at night?",
            children: [
              {
                title: "Tell us more - be as detailed as possible.",
                description: "Tell us more - be as detailed as possible.",
                code: "history-stopbang-stop-breathing-detail",
                type: "textarea",
              },
            ],
          },
          {
            concept: "stopbang.hypertension",
            label:
              "Do you have or are you being treated for high blood pressure?",
          },
          {
            concept: "stopbang.waist",
            label:
              "Is your waist size over 40 inches if you're male, or 35 inches if you're female?",
            children: [
              {
                title: "What's your height in feet?",
                code: "height-feet",
                type: "text",
              },
              {
                title: "What's your height in inches?",
                code: "height-inches",
                type: "text",
              },
              {
                title: "What's your weight in lbs?",
                code: "weight-lbs",
                type: "text",
              },
            ],
          },
          {
            concept: "stopbang.age_over_50",
            label: "Are you older than 50?",
          },
          {
            concept: "stopbang.neck",
            label:
              "Is your neck circumference greater than 17 inches if you're male, or 16 inches if you're female?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "vitals.bp_systolic_lower",
        title:
          "What is the TOP, higher systolic reading of your blood pressure taken in the last 6 months?",
        description:
          "In order to provide a safe and effective treatment plan, we need an accurate blood pressure measurement. Blood pressure results are a higher number over another lower number; for example: 115/75. Please start by entering the higher number below (you’ll enter the lower number on the next screen).",
        code: "systolic",
        type: "bloodpressure-systolic",
        choices: [
          {
            label: "160 or higher",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "140-159",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "120-139",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "95-119",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 95",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        concept: "vitals.bp_diastolic_lower",
        title:
          "What is the BOTTOM, lower diastolic reading of your blood pressure taken in the last 6 months?",
        description:
          "Please select the lower number of your blood pressure reading, it is very important to treat you safely.",
        code: "diastolic",
        type: "bloodpressure-diastolic",
        choices: [
          {
            label: "100 or over",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "90-99",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "80-90",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "65-79",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 65",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        concept: "substance",
        title:
          "Do you drink alcohol, smoke cigarettes, or use other substances?",
        code: "substance-use",
        description:
          "Please answer accurately. It's important we know to treat you safely",
        type: "checkbox",
        choices: [
          {
            concept: "substance.alcohol_regular",
            label: "Do you use alcohol regularly?",
            children: [
              {
                title: "How many drinks a week, on average?",
                code: "alcohol-frequency",
                type: "text",
              },
            ],
          },
          {
            concept: "substance.tobacco",
            label: "Do you smoke or vape?",
            children: [
              {
                title: "How often do you smoke or vape a week, on average? ",
                code: "tobacco-frequency",
                type: "text",
              },
            ],
          },
          {
            concept: "substance.other",
            label: "Any other substances?",
            children: [
              {
                title: "What substances do you use, and how frequently?",
                code: "substances-frequency",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "cage",
        title: "Have you ever: ",
        code: "cage-aid",
        type: "checkbox",
        choices: [
          {
            concept: "cage.cut_down",
            label:
              "Felt you need to cut down on use of a substance (alcohol, cocaine, cannabis, etc)",
          },
          {
            concept: "cage.annoyed",
            label: "Been annoyed by people criticizing your use of substances?",
          },
          {
            concept: "cage.guilty",
            label: "Felt guilty about your use of a substance?",
          },
          {
            concept: "cage.eye_opener",
            label:
              "Needed a drink first thing in the morning as an eye-opener?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        concept: "mood.concern",
        title:
          "Do you have any concerns about your mood, depression, anxiety or worrying?",
        code: "PHQ-GAD-2",
        type: "radio",
        choices: [
          {
            label: "No",
            silent: true,
          },
          {
            label: "Yes",
            children: [
              {
                title:
                  "Over the past two weeks, how often have you felt little interest or pleasure in doing things?",
                code: "PHQ2-1",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt down, depressed or hopeless?",
                type: "radio",
                code: "PHQ2-2",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt nervous, anxious, or on edge?",
                type: "radio",
                code: "GAD2-1",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you not been able to stop or control worrying?",
                code: "GAD2-2",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        concept: "note.doctor",
        title: "Anything else you would like your doctor to know?",
        code: "doctor-let-know-radio",
        description:
          "Before we wrap up, any final things you'd like to tell your doctor?",

        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What would you like to tell your doctor?",
                code: "doctor-let-know",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, we can move on",
            silent: true,
          },
        ],
      },

      {
        concept: "marketing.hear_about",
        title: "How did you FIRST hear about Kick?",
        description:
          "This helps us understand how we can help more people like you.",
        type: "radio",
        code: "hear-about-kick",
        choices: [
          {
            label: "Facebook",
          },
          {
            label: "Google",
          },
          {
            label: "Instagram",
          },
          {
            label: "News Article",
          },
          {
            label: "Pinterest",
          },
          {
            label: "Podcast",
            children: [
              {
                title: "Please tell us which podcast...",
                code: "hear-about-kick-podcast-detail",
                type: "text",
              },
            ],
          },
          {
            label: "Reddit",
          },
          {
            label: "Streaming or Cable TV",
          },
          {
            label: "TikTok",
          },
          {
            label: "Twitter",
          },
          {
            label: "YouTube",
          },
          {
            label: "From a coworker or colleague",
          },
          {
            label: "From a friend or family member",
          },
          {
            label: "Other",
            children: [
              {
                title: "Please tell us where...",
                code: "hear-about-kick-other-detail",
                type: "text",
              },
            ],
          },
        ],
      },
      {
        concept: "marketing.time_known",
        title:
          "For how long did you know about us before making your purchase?",
        description: "",
        type: "radio",
        code: "time-known-before-purchase",
        choices: [
          {
            label: "A day",
          },
          {
            label: "A week",
          },
          {
            label: "1-2 weeks",
          },
          {
            label: "2-4 weeks",
          },
          {
            label: "1-3 months",
          },
          {
            label: "4-12 months",
          },
          {
            label: "More than a year",
          },
        ],
      },
      {
        concept: "motivation.visit_text",
        title:
          "What made you decide to finally take action and complete a visit today?",
        description:
          "This helps us understand your motivations for choosing Kick.",
        type: "textarea",
        code: "visit-motivation",
      },
      {
        concept: "marketing.mbti",
        title: "Are you into MBTI? What's your type? (eg. INTJ, ESFP)",
        description:
          "If you know it, this helps us personalize your treatment plan to fit how your nervous system is wired",
        type: "radio",
        code: "mbti-known-yes-no",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What is your MBTI type?",
                code: "mbti-type",
                type: "radio",
                choices: [
                  {
                    label: "ISTJ - The Inspector",
                  },
                  {
                    label: "ISFJ - The Protector",
                  },
                  {
                    label: "INFJ - The Counselor",
                  },
                  {
                    label: "INTJ - The Mastermind",
                  },
                  {
                    label: "ISTP - The Craftsman",
                  },
                  {
                    label: "ISFP - The Composer",
                  },
                  {
                    label: "INFP - The Healer",
                  },
                  {
                    label: "INTP - The Architect",
                  },
                  {
                    label: "ESTP - The Dynamo",
                  },
                  {
                    label: "ESFP - The Performer",
                  },
                  {
                    label: "ENFP - The Champion",
                  },
                  {
                    label: "ENTP - The Inventor",
                  },
                  {
                    label: "ESTJ - The Supervisor",
                  },
                  {
                    label: "ESFJ - The Provider",
                  },
                  {
                    label: "ENFJ - The Teacher",
                  },
                  {
                    label: "ENTJ - The Commander",
                  },
                ],
              },
            ],
          },
          {
            label: "No",
            silent: true,
          },
        ],
      },
    ],
  };
}

// -------------------------------------------------------
//
// CHANGELOG
//
// -------------------------------------------------------
// Version 2020120101
// - Initial pass
//
// -------------------------------------------------------
// Version 2022010901
// - Fix some typos
// - Reorganized sleep rule outs and parasomnia rule outs, added detailed checkboxes to each choice
// - Made wake up / sleep times a radio selection instead of freeform
// -------------------------------------------------------
// Version 2022011701
// - Make some of the questions less ambigious for the doctor reading visits by adding more context at the end of questions
//
// -------------------------------------------------------
// Version 2022012101
// - Clean up grammatical errors from Katy Suggestions
//
// -------------------------------------------------------
// Version 2022090901
// - Fix what time do you wake up on weekdays to be radio
// - Fix what time do you wake up on weekends to be radio
//
// -------------------------------------------------------
// Version 2023050501
// - Add question on what sleep aids have you tried
// - Reorder questions to put sleep stuff first
// - Add exercise to sleep hygiene question
// - Add question about how long after falling asleep do you wake up
// - Added menopausal option to conditions
// - Add question about how long does it take you to fall back asleep
// - Fix it's critical we know your full history typo
// - Added sleep app option to what have you tried
// - Added "not sure" option to chronotype
//
// -------------------------------------------------------
// Version 2023073101
// - Add question about which sleep medications you are interested in learning more about
//
// -------------------------------------------------------
// Version 2023091901
// - change 12pm to 12am on bedtime questions
//
// Version 2024010701
// - Add a new question specifically on prescription medicines that someone has tried (expand out from existing question)
// - Change the question about medicines interested in to be checkboxes
//
// Version 2024031201
// - Add detailed children for prescription medicines that someone has tried ("did it work? what dosage?")
// - Add children to interested in Gabapentin question
//
// Version 2024031201
// - Make Gabapentin and Cyclobenzaprine unselectable on "sleep medications you are interested in learning more about" in states where they are controlled or PDMP
//
// Version 2025042201
// - Add post purchase survey questions
//
// Version 2025050501
// - Remove duplicate Silenor / Doxepin from sleep medications you are interested in learning more about
// -------------------------------------------------------
//
// Version 2025112301
// - Added MBTI personality type question with Yes/No radio and child question showing all 16 types
// -------------------------------------------------------
//
// Version 2025110101
// - Added Pinterest, News Article, and Streaming or Cable TV to hear about Kick question
// -------------------------------------------------------
//
// Version 2026012001
// - Added Prozasin as medication tried, medication interested in
// -------------------------------------------------------
//
// Version 2026070801
// - Added codes "hear-about-kick", "time-known-before-purchase", and "visit-motivation" to the post-purchase survey questions so the answers can be read into the PostHog purchase-funnel events (and written to the person profile)
// -------------------------------------------------------
//
// Version 2026101001
// - Fixed the spelling of Prazosin (was "Prozasin") in the medications-tried label, its "Did Prazosin work?" follow-up, and the medications-interested list. The follow-up's answer code (insomnia-prescripton-medications-tried-prozasin-detail) is unchanged so existing answers stay linked; getSleepPrescriptionPreferences already reads both spellings
// -------------------------------------------------------
